/**
 * 4STARS — dane strony z public_site/schedule (modit-project)
 *
 * Jeden getDoc na całą sesję, współdzielony przez wszystkie komponenty.
 * Bez onSnapshot — dokument aktualizuje backend, strona czyta raz na wejście.
 */

import { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

// ── JEDNORAZOWE POBRANIE ─────────────────────────────────────────────────────

let schedulePromise = null;

function loadSchedule() {
  if (!schedulePromise) {
    schedulePromise = getDoc(doc(db, "public_site", "schedule"))
      .then((snap) => {
        if (!snap.exists()) throw new Error("Brak dokumentu public_site/schedule");
        const d = snap.data() || {};
        return {
          version: d.version ?? null,
          updatedAt: d.updatedAt?.toDate ? d.updatedAt.toDate() : null,
          regions: Array.isArray(d.regions) ? d.regions : [],
          tags: Array.isArray(d.tags) ? d.tags : [],
          locations: Array.isArray(d.locations) ? d.locations : [],
          groups: Array.isArray(d.groups) ? d.groups : [],
          instructors: Array.isArray(d.instructors) ? d.instructors : [],

          heroSlides: Array.isArray(d.heroSlides) ? d.heroSlides : [],
          stats: Array.isArray(d.stats) ? d.stats : [],
          disciplines: Array.isArray(d.disciplines) ? d.disciplines : [],
          testimonials: Array.isArray(d.testimonials) ? d.testimonials : [],
          news: Array.isArray(d.news) ? d.news : [],
          faq: Array.isArray(d.faq) ? d.faq : [],
          birthday: d.birthday || null,
          events: Array.isArray(d.events) ? d.events : [],
          individual: d.individual || null,
        };
      })
      .catch((e) => {
        schedulePromise = null; // pozwól spróbować ponownie przy następnym wejściu
        throw e;
      });
  }
  return schedulePromise;
}

export function useSchedule() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;

    loadSchedule()
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        console.error("public_site/schedule:", e);
        if (alive) setError(e);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  return { data, loading, error };
}

// ── NARZĘDZIA ────────────────────────────────────────────────────────────────

/** "Pszczelarska 7, Wrocław" → "Wrocław" */
export function cityFromAddress(address) {
  const parts = String(address || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length > 1 ? parts[parts.length - 1] : "";
}

function byName(a, b) {
  return String(a?.name || "").localeCompare(String(b?.name || ""), "pl");
}

// ── SKŁADANIE PODSTRON ───────────────────────────────────────────────────────

/**
 * Lokalizacje o tym samym pageSlug tworzą jedną podstronę z przełącznikiem sal.
 * Lokalizacja z pageSlug === null ma własną podstronę pod swoim slug.
 *
 * Zwraca tablicę obiektów:
 * {
 *   slug, name, address, city, color, photoUrl, description, area,
 *   rooms: [{ id, slug, label }],
 *   isMulti
 * }
 */
function buildPages(locations) {
  const byPage = new Map();

  for (const loc of locations) {
    if (!loc?.slug) continue;
    const key = loc.pageSlug || loc.slug;
    if (!byPage.has(key)) byPage.set(key, []);
    byPage.get(key).push(loc);
  }

  const pages = [];

  for (const [pageSlug, members] of byPage) {
    const primary = members.find((m) => m.slug === pageSlug) || members[0];
    const isMulti = members.length > 1;

    // Przy podstronie wielosalowej `name` to etykieta sali ("Sala główna"),
    // a nazwa całej podstrony siedzi w `pageName` lokalizacji głównej.
    const city = cityFromAddress(primary.address);
    const pageName = primary.pageName || primary.name;

    const rooms = [
      ...members.filter((m) => m.slug === pageSlug),
      ...members.filter((m) => m.slug !== pageSlug).sort(byName),
    ].map((m) => ({
      id: m.id,
      slug: m.slug,
      label: m.name || "Sala",
    }));

    pages.push({
      slug: pageSlug,
      name: pageName,
      address: primary.address || "",
      city,
      color: primary.color || "#C2001C",
      photoUrl: primary.photoUrl || null,
      description: primary.description || "",
      area: primary.area || null,
      lat: Number.isFinite(primary.lat) ? primary.lat : null,
      lng: Number.isFinite(primary.lng) ? primary.lng : null,
      rooms,
      isMulti,
      roomIds: rooms.map((r) => r.id),
    });
  }

  return pages.sort(byName);
}

export function usePages() {
  const { data, loading, error } = useSchedule();

  const pages = useMemo(() => {
    if (!data) return [];
    return buildPages(data.locations);
  }, [data]);

  const regions = data?.regions || [];
  const tags = data?.tags || [];

  return { pages, regions, tags, loading, error };
}

export function usePage(slug) {
  const { pages, loading, error } = usePages();

  const page = useMemo(
    () => (slug ? pages.find((p) => p.slug === slug) || null : null),
    [pages, slug]
  );

  return { page, loading, error, notFound: !loading && !error && !page };
}

// ── GRAFIK DLA SALI ──────────────────────────────────────────────────────────

/**
 * Zwraca terminy dla wskazanej sali, rozbite na pojedyncze wpisy
 * i wzbogacone o dane grupy.
 */
export function sessionsForRoom(groups, roomId) {
  if (!roomId) return [];

  const out = [];

  for (const g of groups) {
    for (const s of g.sessions || []) {
      if (s?.locationId !== roomId) continue;

      out.push({
        key: `${g.id}-${s.dayOfWeek}-${s.startTime}`,
        groupId: g.id,
        groupName: g.name || "",
        category: g.category || null,
        exp: g.exp || null,
        adults: !!g.adults,
        minAge: g.minAge ?? null,
        maxAge: g.maxAge ?? null,
        dayOfWeek: s.dayOfWeek,
        startTime: s.startTime || "",
        endTime: s.endTime || "",
        instructor: s.instructor || null,
      });
    }
  }

  return out;
}

/** Grupy, które mają jakikolwiek termin w którejkolwiek sali tej podstrony. */
export function groupsForPage(groups, roomIds) {
  if (!Array.isArray(roomIds) || roomIds.length === 0) return [];

  const ids = new Set(roomIds);

  return groups.filter((g) =>
    (g.sessions || []).some((s) => ids.has(s?.locationId))
  );
}

// ── WYDARZENIA ───────────────────────────────────────────────────────────────

/** Dzisiejsza data w formacie YYYY-MM-DD, w strefie lokalnej. */
function today() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Stan wydarzenia. Przy status "auto" liczony z dat:
 * wszystkie terminy minęły → archiwalne.
 */
export function resolveEventStatus(event) {
  if (!event) return "past";
  if (event.status && event.status !== "auto") return event.status;

  const dates = event.dates || [];
  if (dates.length === 0) return "upcoming";

  const now = today();
  return dates.some((d) => (d?.endDate || d?.date) >= now) ? "upcoming" : "past";
}

/** Najbliższy termin wydarzenia, albo ostatni przy archiwalnych. */
export function mainEventDate(event) {
  const dates = (event?.dates || []).filter((d) => d && d.date);
  if (dates.length === 0) return null;

  const now = today();
  const sorted = [...dates].sort((a, b) => a.date.localeCompare(b.date));

  return sorted.find((d) => (d.endDate || d.date) >= now) || sorted[sorted.length - 1];
}

/** Trenerki prowadzące zajęcia w którejkolwiek sali tej podstrony. */
export function instructorsForPage(groups, instructors, roomIds) {
  if (!Array.isArray(roomIds) || roomIds.length === 0) return [];

  const ids = new Set(roomIds);
  const names = new Set();

  for (const g of groups) {
    for (const s of g.sessions || []) {
      if (ids.has(s?.locationId) && s.instructor) names.add(s.instructor);
    }
  }

  return instructors.filter((i) => names.has(i.name));
}